import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Search, UserCheck, Undo2, RefreshCw, QrCode, LogOut, DoorOpen } from "lucide-react";
import { Panel, Eyebrow, MonoLabel, KPI, LiveDot } from "@/components/Panel";
import { LoginGate } from "@/pages/Scanner";
import { scannerApi, scannerAuth } from "@/lib/scannerApi";
import { apiErr } from "@/lib/authApi";

const API = process.env.REACT_APP_BACKEND_URL;
const clock = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—");
const photo = (url) => (url ? (url.startsWith("/api") ? `${API}${url}` : url) : null);

export default function ReceptionDesk() {
  const [authed, setAuthed] = useState(!!scannerAuth.token);
  const [exchanging, setExchanging] = useState(() => !scannerAuth.token && !!(localStorage.getItem("bni-admin-token") || localStorage.getItem("bni-devadmin-token")));

  useEffect(() => {
    if (authed) return;
    const adminTok = localStorage.getItem("bni-admin-token") || localStorage.getItem("bni-devadmin-token");
    if (!adminTok) { setExchanging(false); return; }
    let cancelled = false;
    scannerAuth.exchangeFromAdmin()
      .then(() => { if (!cancelled) setAuthed(true); })
      .catch(() => { if (!cancelled) setExchanging(false); })
      .finally(() => { if (!cancelled) setExchanging(false); });
    return () => { cancelled = true; };
  }, [authed]);

  if (!authed && exchanging) return <div className="min-h-screen flex items-center justify-center mono-label" style={{ color: "var(--taupe)" }}>Signing you in…</div>;
  if (!authed) return <LoginGate onSuccess={() => setAuthed(true)} />;
  return <Desk onLogout={() => { scannerAuth.clear(); setAuthed(false); }} />;
}

function Desk({ onLogout }) {
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [acting, setActing] = useState(null);

  const load = useCallback(async () => {
    try { setStats(await scannerApi.entryStats(12)); }
    catch (e) { toast.error(apiErr(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  // debounced search on name / phone
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults(null); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try { setResults((await scannerApi.entrySearch(term)).items); }
      catch (e) { toast.error(apiErr(e)); } finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const refreshSearch = async () => {
    if (q.trim().length < 2) return;
    try { setResults((await scannerApi.entrySearch(q.trim())).items); } catch { /* keep old list */ }
  };

  const checkIn = async (r) => {
    setActing(r.registration_id);
    try {
      const res = await scannerApi.entryManual(r.registration_id);
      if (res.status === "already_checked_in") toast.info(`${r.name} already arrived at ${clock(res.first_scan_at)}`);
      else toast.success(`${r.name} checked in`);
      await Promise.all([load(), refreshSearch()]);
    } catch (e) { toast.error(apiErr(e)); } finally { setActing(null); }
  };

  const undo = async (r) => {
    setActing(r.registration_id);
    try {
      await scannerApi.entryUndo(r.registration_id);
      toast.success(`Check-in undone for ${r.name}`);
      await Promise.all([load(), refreshSearch()]);
    } catch (e) { toast.error(apiErr(e)); } finally { setActing(null); }
  };

  const pct = useMemo(() => {
    if (!stats || !stats.expected) return 0;
    return Math.min(100, Math.round((stats.arrived / stats.expected) * 100));
  }, [stats]);

  return (
    <div className="min-h-screen">
      <Toaster theme="dark" position="top-right" />
      <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-10">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <Eyebrow>Reception Desk · Entry</Eyebrow>
            <h1 className="h-hero mt-3 text-4xl sm:text-5xl">Welcome them in.</h1>
            <div className="mono-label mt-2" style={{ color: "var(--taupe)" }}>
              Signed in as <span style={{ color: "var(--text)" }}>{scannerAuth.userName || "desk"}</span> · live counts refresh every 10s
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <LiveDot label="LIVE" />
            <Link to="/scanner" className="btn-ghost text-xs" data-testid="reception-scanner-link"><QrCode size={12} /> QR scanner</Link>
            <button data-testid="reception-refresh-btn" className="btn-ghost text-xs" onClick={load}><RefreshCw size={12} /> Refresh</button>
            <button data-testid="reception-logout-btn" className="btn-ghost text-xs" onClick={onLogout}><LogOut size={12} /> Sign out</button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI label="Arrived" value={stats ? stats.arrived : "—"} accent="green" hint={`${pct}% of expected`} />
          <KPI label="Yet to arrive" value={stats ? stats.pending : "—"} />
          <KPI label="Expected (paid)" value={stats ? stats.expected : "—"} />
          <KPI label="Last 15 minutes" value={stats ? stats.last_15_min : "—"} accent="red" hint="arrival rate" />
        </div>

        <Panel className="mt-4 p-5">
          <div className="flex items-center justify-between">
            <MonoLabel>Arrivals progress</MonoLabel>
            <MonoLabel style={{ color: "var(--taupe)" }} data-testid="reception-progress-label">{stats ? `${stats.arrived} / ${stats.expected}` : "—"}</MonoLabel>
          </div>
          <div className="mt-3 h-2 w-full" style={{ background: "var(--panel-2)" }}>
            <div className="h-2 transition-all duration-700" data-testid="reception-progress-bar"
              style={{ width: `${pct}%`, background: "var(--red)" }} />
          </div>
        </Panel>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Search / manual check-in */}
          <Panel className="p-6 lg:col-span-7">
            <MonoLabel><Search size={13} className="inline mr-2" />Can't scan the QR? Find them by name or phone</MonoLabel>
            <input data-testid="reception-search-input" className="input-x mt-4" autoFocus
              placeholder="Type a name or mobile number…" value={q} onChange={(e) => setQ(e.target.value)} />

            <div className="mt-5 space-y-2">
              {q.trim().length >= 2 && searching && <div className="mono-label" data-testid="reception-searching">Searching…</div>}
              {results && results.length === 0 && (
                <div className="mono-label" style={{ color: "var(--taupe)" }} data-testid="reception-no-results">
                  No paid registration matches "{q.trim()}" — check the spelling or the number they registered with.
                </div>
              )}
              {(results || []).map((r) => (
                <div key={r.registration_id} className="flex items-center gap-3 hairline p-3" data-testid={`reception-result-${r.registration_id}`}>
                  {photo(r.photo_url) ? (
                    <img src={photo(r.photo_url)} alt="" className="w-10 h-10 object-cover border border-[var(--hairline)]" />
                  ) : (
                    <div className="w-10 h-10 border border-[var(--hairline)] grid place-items-center font-serif" style={{ background: "var(--panel-2)", color: "var(--red-2)" }}>
                      {(r.name || "?").trim().charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">{r.name}</div>
                    <div className="mono-label truncate" style={{ color: "var(--taupe)" }}>
                      {r.phone} {r.chapter ? `· ${r.chapter}` : ""} {r.registration_type === "stall" ? "· STALL" : ""}
                    </div>
                  </div>
                  {r.arrived_at ? (
                    <div className="flex items-center gap-2">
                      <span className="chip green" data-testid={`reception-arrived-${r.registration_id}`}>ARRIVED · {clock(r.arrived_at)}</span>
                      <button className="btn-ghost !py-1 !px-2 mono-label" disabled={acting === r.registration_id}
                        data-testid={`reception-undo-${r.registration_id}`} onClick={() => undo(r)}>
                        <Undo2 size={12} /> Undo
                      </button>
                    </div>
                  ) : (
                    <button className="btn-red !py-2 !px-3 text-xs" disabled={acting === r.registration_id}
                      data-testid={`reception-checkin-${r.registration_id}`} onClick={() => checkIn(r)}>
                      <UserCheck size={13} /> {acting === r.registration_id ? "Saving…" : "Check in"}
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mono-label mt-5" style={{ color: "var(--muted)" }}>
              Manual check-ins are recorded as <code>manual</code> with your name in the audit trail, so they stay
              distinguishable from QR scans. Meals only open after a member has entry check-in.
            </div>
          </Panel>

          {/* Live arrivals feed */}
          <Panel className="p-6 lg:col-span-5">
            <div className="flex items-center justify-between">
              <MonoLabel><DoorOpen size={13} className="inline mr-2" />Latest arrivals</MonoLabel>
              <MonoLabel style={{ color: "var(--taupe)" }}>{clock(stats?.fetched_at)}</MonoLabel>
            </div>
            <div className="mt-4 space-y-2" data-testid="reception-feed">
              {!stats && <div className="mono-label">Loading…</div>}
              {stats && stats.recent.length === 0 && (
                <div className="mono-label" style={{ color: "var(--taupe)" }} data-testid="reception-feed-empty">
                  Nobody has checked in yet — the first arrival will appear here instantly.
                </div>
              )}
              {(stats?.recent || []).map((r, i) => (
                <div key={`${r.registration_id}-${i}`} className="flex items-center gap-3 hairline p-3" data-testid={`reception-feed-${i}`}>
                  {photo(r.photo_url) ? (
                    <img src={photo(r.photo_url)} alt="" className="w-9 h-9 object-cover border border-[var(--hairline)]" />
                  ) : (
                    <div className="w-9 h-9 border border-[var(--hairline)] grid place-items-center font-serif text-sm" style={{ background: "var(--panel-2)", color: "var(--red-2)" }}>
                      {(r.name || "?").trim().charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">{r.name || r.registration_id.slice(0, 8).toUpperCase()}</div>
                    <div className="mono-label truncate" style={{ color: "var(--taupe)" }}>{r.chapter || "—"} · {r.source === "manual" ? "desk" : "QR"}</div>
                  </div>
                  <span className="mono-label" style={{ color: "var(--green)" }}>{clock(r.scanned_at)}</span>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
